import React from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { ArrowLeft } from "lucide-react";
import "../styles/CaseStudy.css";

// Local images
import blindbarber from "../assets/case-studies/blindbarber.jpg";
import tillerman from "../assets/case-studies/tillerman.jpg";
import townsend from "../assets/case-studies/townsend.jpg";
import shantytown from "../assets/case-studies/shantytown.jpg";
import scissorsscotch from "../assets/case-studies/scissorsscotch.jpg";
import twispriver from "../assets/case-studies/twispriver.jpg";
import honeyrun from "../assets/case-studies/honeyrun.jpg";
import gracecottage from "../assets/case-studies/gracecottage.jpg";
import twispinn from "../assets/case-studies/twispinn.jpg";
import modoc from "../assets/case-studies/modoc.jpg";
import defaultImg from "../assets/case-studies/default.jpg";

const CaseStudy = () => {
  const { projectId } = useParams();

  const caseStudies = {
    blindbarber: {
      title: "Blind Barber",
      tagline: "Barbershop + ecommerce grooming store",
      image: blindbarber,
      overview:
        "Premium barbershop brand with online ecommerce for grooming products and memberships.",
      role: "Web design, ecommerce integration, performance optimization",
      tech: ["React", "Shopify", "Tailwind CSS", "Cloudflare"],
      problem:
        "Brand needed a modern ecommerce experience and unified booking across multiple locations.",
      approach:
        "Built a clean UI with fast transitions, optimization and frictionless checkout.",
      results: [
        "Online sales increased by 47%",
        "Booking process improved user satisfaction",
        "Better brand consistency and visual presence",
      ],
      testimonial: {
        text: "The performance upgrade dramatically improved conversions.",
        author: "Brand Director",
        position: "Blind Barber",
      },
    },

    thetillermanvt: {
      title: "The Tillerman VT",
      tagline: "Fine dining restaurant reservation website",
      image: tillerman,
      overview:
        "A modern restaurant website built to improve reservations and brand storytelling.",
      role: "UI/UX design, SEO optimization",
      tech: ["React", "Tailwind", "SEO"],
      problem: "Needed better conversion and mobile performance.",
      approach:
        "Designed visuals, optimized images and added structured metadata for SEO.",
      results: [
        "Online bookings increased by 33%",
        "Bounce rate reduced",
        "Mobile-first UX enhanced engagement",
      ],
      testimonial: {
        text: "Beautiful work — guests compliment how easy it is to reserve.",
        author: "Restaurant Manager",
        position: "The Tillerman VT",
      },
    },

    townsendbarandgrill: {
      title: "Townsend Bar & Grill",
      tagline: "Bar & grill restaurant branding site",
      image: townsend,
      overview: "Local restaurant website showcasing menu and weekend events.",
      role: "Redesign, UX optimization, menu system",
      tech: ["React", "Tailwind"],
      problem: "Old site outdated with poor mobile usability.",
      approach: "Modern layout, menu browsing and social promotions.",
      results: ["Website traffic +50%", "More walk-in customers"],
      testimonial: {
        text: "Premium and functional — huge upgrade!",
        author: "Owner",
        position: "Townsend Bar & Grill",
      },
    },

    shantytownbarandgrill: {
      title: "Shantytown Bar & Grill",
      tagline: "Restaurant website redesign",
      image: shantytown,
      overview: "Website for historic and busy bar & grill.",
      role: "Web redesign, mobile optimization",
      tech: ["React", "Tailwind", "Cloud Hosting"],
      problem: "Outdated site with no menu or event structure.",
      approach: "Improved layout flow & enhanced visuals",
      results: ["SEO visibility improved", "Menu viewing time increased"],
      testimonial: {
        text: "Professional, clean, fast — exactly what we wanted!",
        author: "Business Owner",
        position: "Shantytown Bar & Grill",
      },
    },

    scissorsscotch: {
      title: "Scissors & Scotch",
      tagline: "Luxury barbershop booking experience",
      image: scissorsscotch,
      overview: "Premium grooming lounge with booking system.",
      role: "Web development, booking enhancement",
      tech: ["React", "Node.js", "Booking API"],
      problem: "Wanted unified booking & speed optimization.",
      approach: "Streamlined workflow & improved UI consistency.",
      results: ["Appointments +35%", "Load time +63%"],
      testimonial: {
        text: "Massive improvement in clarity & performance!",
        author: "Operations Manager",
        position: "Scissors & Scotch",
      },
    },

    twispriversuites: {
      title: "Twisp River Suites",
      tagline: "Hotel & accommodation booking",
      image: twispriver,
      overview: "Boutique lodging website with direct reservations.",
      role: "Design, booking integration, SEO",
      tech: ["React", "Booking Engine", "Analytics"],
      problem: "High commission fees from third-party bookings.",
      approach: "Direct booking system & redesigned UI.",
      results: ["Direct bookings doubled", "Reduced OTA costs"],
      testimonial: {
        text: "Saved us money from platform fees — amazing!",
        author: "Hotel Owner",
        position: "Twisp River Suites",
      },
    },

    innathoneyrun: {
      title: "Inn at Honey Run",
      tagline: "Luxury countryside resort",
      image: honeyrun,
      overview: "Elegant resort site focused on visuals & booking.",
      role: "Redesign, photo UI, SEO audit",
      tech: ["React", "Tailwind", "Booking Engine"],
      problem: "UX didn’t support luxury experience.",
      approach: "Visually rich layout & gallery architecture.",
      results: ["Time on page x3", "Higher booking engagement"],
      testimonial: {
        text: "Stunning transformation with real impact.",
        author: "Marketing Director",
        position: "Inn at Honey Run",
      },
    },

    gracecottage: {
      title: "Grace Cottage",
      tagline: "Retreat wellness stays",
      image: gracecottage,
      overview: "Website focused on relaxation & retreat visitors.",
      role: "UI redesign, branding",
      tech: ["React", "Tailwind"],
      problem: "Lacked emotional appeal and pro mobile design.",
      approach: "Immersive visuals & restructured flow.",
      results: ["More stay requests", "Improved first impression"],
      testimonial: {
        text: "Warm and premium!",
        author: "Property Owner",
        position: "Grace Cottage",
      },
    },

    twispriverinn: {
      title: "Twisp River Inn",
      tagline: "Charming inn website project",
      image: twispinn,
      overview: "Modernized lodging experience.",
      role: "UX redesign, info architecture",
      tech: ["React", "Tailwind", "Hosting"],
      problem: "Lacked digital identity & booking clarity.",
      approach: "Simplified navigation & visuals.",
      results: ["More inquiries", "Higher conversions"],
      testimonial: {
        text: "Easy for customers to navigate & contact.",
        author: "Inn Owner",
        position: "Twisp River Inn",
      },
    },

    modocmedicalcenter: {
      title: "Modoc Medical Center",
      tagline: "Healthcare facility website",
      image: modoc,
      overview:
        "Redesigned hospital website improving access & support.",
      role: "Design + development, patient experience",
      tech: ["React", "SEO", "Accessibility"],
      problem: "Hard navigation caused patient frustration.",
      approach:
        "Improved accessibility & optimized performance.",
      results: ["Support calls -30%", "Better access to services"],
      testimonial: {
        text: "Huge improvement for patients — very thankful.",
        author: "Communications Director",
        position: "Modoc Medical Center",
      },
    },
  };

  const project = caseStudies[projectId] || { image: defaultImg };

  return (
    <div className="case-study-page">
      <Navbar />

      <section
        className="case-study-hero"
        style={{ backgroundImage: `url(${project.image})` }}
      >
        <div className="hero-overlay" />
        <div className="container hero-content">
          <Link to="/work" className="back-link">
            <ArrowLeft size={20} />
            Back to Work
          </Link>
          <h1>{project.title}</h1>
          <p>{project.tagline}</p>

          <div className="case-tech-tags">
            {project.tech?.map((tech, i) => (
              <span className="tech-tag" key={i}>
                {tech}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CONTENT SECTION */}
      <section className="case-study-content">
        <div className="container">
          <div className="content-layout">
            <div className="main-content">
              <div className="content-section">
                <h2>Overview</h2>
                <p>{project.overview}</p>
              </div>

              <div className="content-section">
                <h2>The Challenge</h2>
                <p>{project.problem}</p>
              </div>

              <div className="content-section">
                <h2>Our Approach</h2>
                <p>{project.approach}</p>
              </div>

              <div className="content-section">
                <h2>Results & Impact</h2>
                <ul className="results-list">
                  {project.results?.map((result, i) => (
                    <li key={i}>{result}</li>
                  ))}
                </ul>
              </div>

              {project.testimonial && (
                <div className="testimonial-box">
                  <p>"{project.testimonial.text}"</p>
                  <div>
                    <strong>{project.testimonial.author}</strong> —{" "}
                    <span>{project.testimonial.position}</span>
                  </div>
                </div>
              )}
            </div>

            <aside className="sidebar">
              <div className="project-info-card">
                <h3>Project Info</h3>
                <p><strong>Role:</strong> {project.role}</p>
                <strong>Tech Stack:</strong>
                <div className="tech-list">
                  {project.tech?.map((t, i) => (
                    <span key={i}>{t}</span>
                  ))}
                </div>
              </div>

              <Link to="/work" className="btn btn-secondary">
                More Projects
              </Link>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudy;
